import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
	Box,
	Paper,
	Typography,
	TextField,
	Button,
	Select,
	MenuItem,
	FormControl,
	InputLabel,
	CircularProgress,
	Autocomplete,
} from '@mui/material';
import InputMask from 'react-input-mask';
import { useQuery } from 'react-query';
import {
	IManagerInitiationData,
	EmployeeType,
	EmploymentType,
	RoleType,
} from '../interfaces/IOnboardingFlow';
import style from './ManagerInitiationForm.module.scss';
import { getUsersByWorkplace } from 'core/API/checklist';
import useUserDataStore from 'store/useUserDataStore';
import { getOnboardingSettings } from 'core/API/onboarding';
import { GetAllWorkplaces } from 'core/API/workplaces';
import { Workplace } from 'module/workplaces/interface/WorkplacesInterface';
import { IOnboardingSettings } from 'core/interfaces/onboardingInterface';
import { getCurrentUserHierarchyId, getPermittedHierarchies } from '../utils/hierarchyPermissions';
import { getHierarchyOptionLabel } from 'module/settings/settingsGameMasterTabs/GeneralSetting/UserSettings/hierarchySelection';
import { DEFAULT_SECURITY_ROLES } from 'module/settings/settingsGameMasterTabs/GeneralSetting/constants';
import { getJobCodes } from 'core/API/scheduler';
import EligibleJobCodesPicker from 'module/schedule/components/EligibleJobCodesPicker/EligibleJobCodesPicker';
import { isSchedulingModuleLicensed } from '../utils/onboardingAvailability';
import { useWorkplaceUser } from 'module/documents/utils';

const EXCLUDED_ONBOARDING_SECURITY_ROLE_TYPES = [
	DEFAULT_SECURITY_ROLES.ORGANISATION_ADMIN,
	DEFAULT_SECURITY_ROLES.DEVICE_USER,
];

interface ManagerInitiationFormProps {
	workplaceId: string;
	onSubmit: (data: IManagerInitiationData) => Promise<void>;
	onCancel: () => void;
}

export function ManagerInitiationForm({
	workplaceId,
	onSubmit,
	onCancel,
}: ManagerInitiationFormProps): JSX.Element {
	// Clean hierarchy name: remove parenthetical content and standalone 'node'
	const cleanHierarchyName = (raw?: string) => {
		if (!raw) return '';
		let s = raw.replace(/\s*\([^)]*\)\s*/g, ' ');
		s = s.replace(/\bnode\b/gi, ' ');
		return s.replace(/\s+/g, ' ').trim();
	};

	const { securityRoles, hierarchyNodes, initializeFromLocalStorage, currentUserRole } =
		useUserDataStore();
	const { userOrg } = useWorkplaceUser();
	const schedulingLicensed = isSchedulingModuleLicensed(userOrg?.products);

	const { data: jobCodesData = [] } = useQuery('jobCodes', () => getJobCodes(), {
		enabled: schedulingLicensed,
		staleTime: 5 * 60 * 1000,
	});

	const currentWorkplaceHierarchy = hierarchyNodes.find(
		(node) =>
			node.childId === Number(workplaceId) ||
			(node.type === 'workplace' && node.childId === Number(workplaceId))
	);

	// Get current user's hierarchy ID and filter permitted hierarchies
	const currentUserHierarchyId = getCurrentUserHierarchyId();

	const permittedHierarchies = currentUserHierarchyId
		? getPermittedHierarchies(currentUserHierarchyId, hierarchyNodes)
		: hierarchyNodes; // If no current user hierarchy, show all (fallback)

	// Get today's date in YYYY-MM-DD format for the employment start date default
	const getTodayDate = () => {
		const today = new Date();
		const year = today.getFullYear();
		const month = String(today.getMonth() + 1).padStart(2, '0');
		const day = String(today.getDate()).padStart(2, '0');
		return `${year}-${month}-${day}`;
	};

	const [formData, setFormData] = useState<IManagerInitiationData>({
		employeeType: EmployeeType.NEW,
		firstName: '',
		middleName: '',
		lastName: '',
		email: '',
		cellPhone: '',
		employmentStartDate: getTodayDate(),
		employmentType: EmploymentType.HOURLY,
		roleType: RoleType.PART_TIME,
		securityRole: '',
		hierarchyAssignment: '',
		eligibleJobCodes: [],
	});

	const [employees, setEmployees] = useState<any[]>([]);
	const [isLoadingEmployees, setIsLoadingEmployees] = useState(false);
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [errors, setErrors] = useState<Record<string, string>>({});
	const [workplace, setWorkplace] = useState<Workplace | null>(null);
	const [onboardingSettings, setOnboardingSettings] = useState<IOnboardingSettings | null>(null);
	const [isLoadingWorkplaceInfo, setIsLoadingWorkplaceInfo] = useState(true);

	// Filter security roles based on workplace
	// Include roles where workplace is null (available to all) or matches current workplace
	const isCurrentUserManager = currentUserRole?.securityRoleType?.label === 'Manager';
	const isCurrentUserOrgAdmin =
		currentUserRole?.securityRoleType?.label === 'Organization Admin' ||
		currentUserRole?.name === DEFAULT_SECURITY_ROLES.ORGANISATION_ADMIN;

	const filteredSecurityRoles = securityRoles.filter((role) => {
		// Filter by workplace: include roles available to all workplaces or matching current workplace
		if (role.workplace && role.workplace !== `/api/workplaces/${workplaceId}`) return false;
		// Exclude organization admin and device role types from onboarding assignment
		if (EXCLUDED_ONBOARDING_SECURITY_ROLE_TYPES.includes(role?.name || '')) {
			return false;
		}
		// If the current user is not a Manager, exclude roles with securityRoleType "Manager"
		if (!isCurrentUserManager && role.securityRoleType?.label === 'Manager') return false;
		return true;
	});

	// Managers can only assign to their current workplace; org admins can select any hierarchy level
	const hierarchyOptions = isCurrentUserOrgAdmin
		? permittedHierarchies
		: currentWorkplaceHierarchy
			? [currentWorkplaceHierarchy]
			: permittedHierarchies.filter(
					(node) => node.type === 'workplace' && node.childId === Number(workplaceId)
				);

	const fetchEmployees = useCallback(async () => {
		setIsLoadingEmployees(true);
		try {
			const response = await getUsersByWorkplace(workplaceId);
			setEmployees(response);
		} catch (error) {
			console.error('Error fetching employees:', error);
			setEmployees([]);
		} finally {
			setIsLoadingEmployees(false);
		}
	}, [workplaceId]);

	// Fetch workplace and onboarding settings
	useEffect(() => {
		const fetchWorkplaceInfo = async () => {
			setIsLoadingWorkplaceInfo(true);
			try {
				// Try to get workplaces from cache first
				let workplacesMap: Record<string, Workplace> = {};
				const cachedWorkplaces = localStorage.getItem('cached-workplaces');

				if (cachedWorkplaces) {
					try {
						workplacesMap = JSON.parse(cachedWorkplaces);
					} catch (e) {
						console.error('Error parsing cached workplaces:', e);
					}
				}

				// If no cache or workplace not in cache, fetch all workplaces
				if (!workplacesMap[workplaceId]) {
					const members = await GetAllWorkplaces();
					if (members.length > 0) {
						workplacesMap = members.reduce(
							(acc: Record<string, Workplace>, workplace: Workplace) => {
								acc[workplace.id.toString()] = workplace;
								return acc;
							},
							{}
						);
						// Cache the workplaces
						localStorage.setItem('cached-workplaces', JSON.stringify(workplacesMap));
					}
				}

				const workplaceData = workplacesMap[workplaceId];
				const settingsData = await getOnboardingSettings(Number(workplaceId));

				setWorkplace(workplaceData);
				setOnboardingSettings(settingsData);
			} catch (error) {
				console.error('Error fetching workplace info:', error);
			} finally {
				setIsLoadingWorkplaceInfo(false);
			}
		};

		fetchWorkplaceInfo();
	}, [workplaceId]);

	// Initialize data from localStorage on mount
	useEffect(() => {
		initializeFromLocalStorage();
	}, [initializeFromLocalStorage]);

	// Preselect current workplace hierarchy when hierarchy nodes are loaded
	useEffect(() => {
		if (currentWorkplaceHierarchy && !formData.hierarchyAssignment) {
			setFormData((prev) => ({
				...prev,
				hierarchyAssignment: currentWorkplaceHierarchy['@id'] || '',
			}));
		}
	}, [currentWorkplaceHierarchy, formData.hierarchyAssignment]);

	// Fetch existing employees for rehire/existing selection
	useEffect(() => {
		if (formData.employeeType !== EmployeeType.NEW) {
			fetchEmployees();
		}
	}, [formData.employeeType, fetchEmployees]);

	const handleEmployeeSelect = (employee: any) => {
		if (employee) {
			setFormData({
				...formData,
				existingEmployeeId: employee.id,
				firstName: employee.firstName || '',
				lastName: employee.lastName || '',
				email: (employee.email || '').toLowerCase(),
				cellPhone: employee.phone || '',
			});
		}
	};

	const handleInputChange = (field: keyof IManagerInitiationData, value: any) => {
		setFormData({
			...formData,
			[field]: value,
		});
		// Clear error for this field
		if (errors[field]) {
			setErrors({
				...errors,
				[field]: '',
			});
		}
	};

	const selectedJobCodeIds = formData.eligibleJobCodes || [];

	const handleJobCodesChange = (nextIds: number[]) => {
		handleInputChange('eligibleJobCodes', nextIds);
	};

	const validatePhoneNumber = (value: string): boolean => {
		if (!value) return false;
		// Match EmployeeSelfEntryForm validation: exactly 10 digits
		return value.replace(/[^0-9]/g, '').length === 10;
	};

	const validateForm = (): boolean => {
		const newErrors: Record<string, string> = {};

		// Required fields validation
		if (!formData.firstName.trim()) newErrors.firstName = 'First name is required';
		if (!formData.lastName.trim()) newErrors.lastName = 'Last name is required';
		if (!formData.email.trim()) newErrors.email = 'Email is required';
		else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email))
			newErrors.email = 'Invalid email format';

		if (!formData.cellPhone.trim()) {
			newErrors.cellPhone = 'Cell phone is required';
		} else if (!validatePhoneNumber(formData.cellPhone)) {
			newErrors.cellPhone = 'Please enter a complete phone number';
		}

		if (!formData.employmentStartDate) newErrors.employmentStartDate = 'Start date is required';
		if (!formData.securityRole) newErrors.securityRole = 'Security role is required';
		if (!formData.hierarchyAssignment) {
			newErrors.hierarchyAssignment = 'Hierarchy assignment is required';
		} else {
			// Validate that the selected hierarchy is in the allowed list
			const isPermitted = hierarchyOptions.some(
				(node) => node['@id'] === formData.hierarchyAssignment
			);
			if (!isPermitted) {
				newErrors.hierarchyAssignment = 'You do not have permission to assign this hierarchy level';
			}
		}

		setErrors(newErrors);
		return Object.keys(newErrors).length === 0;
	};

	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault();

		if (!validateForm()) {
			return;
		}

		setIsSubmitting(true);
		try {
			// Ensure email is lowercase before submitting
			const submitData = {
				...formData,
				email: formData.email.toLowerCase(),
			};
			await onSubmit(submitData);
		} catch (error) {
			console.error('Error submitting form:', error);
		} finally {
			setIsSubmitting(false);
		}
	};

	return (
		<div className={style.FormContainer}>
			<div className={style.Header}>
				<div className={style.HeaderTop}>
					<Button onClick={onCancel} className={style.BackButton}>
						Back
					</Button>
					<Button
						type="submit"
						form="manager-initiation-form"
						variant="contained"
						disabled={isSubmitting}
						className={style.SubmitButton}
					>
						{isSubmitting ? <CircularProgress size={24} color="inherit" /> : 'Start Onboarding'}
					</Button>
				</div>
				<Typography variant="h5" fontWeight={600}>
					Initiate Onboarding - Phase 1
				</Typography>
			</div>

			{/* Workplace Information */}
			{isLoadingWorkplaceInfo ? (
				<Box sx={{ textAlign: 'center', py: 2 }}>
					<CircularProgress size={24} />
				</Box>
			) : workplace ? (
				<Paper sx={{ p: 2.5, mb: 2, backgroundColor: '#f8f9fa' }}>
					<Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1.5, color: '#333' }}>
						Workplace Information
					</Typography>
					<Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 2 }}>
						<Box>
							<Typography variant="caption" sx={{ color: '#666', display: 'block' }}>
								Workplace Name
							</Typography>
							<Typography variant="body2" sx={{ fontWeight: 500 }}>
								{workplace.name || 'N/A'}
							</Typography>
						</Box>
						<Box>
							<Typography variant="caption" sx={{ color: '#666', display: 'block' }}>
								Address
							</Typography>
							<Typography variant="body2" sx={{ fontWeight: 500 }}>
								{workplace.streetAddress && workplace.city
									? `${workplace.streetAddress}, ${workplace.city}${workplace.state ? ', ' + workplace.state : ''} ${workplace.postcode || ''}`
									: 'N/A'}
							</Typography>
						</Box>
						<Box>
							<Typography variant="caption" sx={{ color: '#666', display: 'block' }}>
								FEIN
							</Typography>
							<Typography variant="body2" sx={{ fontWeight: 500 }}>
								{onboardingSettings?.ein || 'Not configured'}
							</Typography>
						</Box>
					</Box>
				</Paper>
			) : null}

			<Paper className={style.FormPaper}>
				<form id="manager-initiation-form" onSubmit={handleSubmit}>
					<Typography variant="h6" sx={{ mb: 3, fontWeight: 600 }}>
						Manager Initiation
					</Typography>

					{/* Employee Type Selection — hidden until Rehire/Existing are implemented */}

					{/* Basic Information */}
					<Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600 }}>
						Basic Information
					</Typography>

					<Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 2, mb: 3 }}>
						<TextField
							label="First Name"
							value={formData.firstName}
							onChange={(e) => handleInputChange('firstName', e.target.value)}
							error={!!errors.firstName}
							helperText={errors.firstName}
							required
						/>
						<TextField
							label="Middle Name"
							value={formData.middleName}
							onChange={(e) => handleInputChange('middleName', e.target.value)}
						/>
						<TextField
							label="Last Name"
							value={formData.lastName}
							onChange={(e) => handleInputChange('lastName', e.target.value)}
							error={!!errors.lastName}
							helperText={errors.lastName}
							required
						/>
					</Box>

					<Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, mb: 3 }}>
						<TextField
							label="Email Address"
							type="email"
							value={formData.email}
							onChange={(e) => handleInputChange('email', e.target.value.toLowerCase())}
							error={!!errors.email}
							helperText={errors.email || 'Must be unique'}
							required
						/>
						<InputMask
							mask="(999) 999 - 9999"
							value={formData.cellPhone}
							onChange={(e) => handleInputChange('cellPhone', e.target.value)}
							maskChar="_"
						>
							{(inputProps: any) => (
								<TextField
									{...inputProps}
									label="Cell Phone"
									error={!!errors.cellPhone}
									helperText={errors.cellPhone}
									required
								/>
							)}
						</InputMask>
					</Box>

					{/* Employment Details */}
					<Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600 }}>
						Employment Details
					</Typography>

					<Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 2, mb: 3 }}>
						<TextField
							label="Employment Start Date"
							type="date"
							value={formData.employmentStartDate}
							onChange={(e) => handleInputChange('employmentStartDate', e.target.value)}
							error={!!errors.employmentStartDate}
							helperText={errors.employmentStartDate}
							InputLabelProps={{ shrink: true }}
							required
						/>
						<FormControl>
							<InputLabel>Employment Type</InputLabel>
							<Select
								value={formData.employmentType}
								onChange={(e) => handleInputChange('employmentType', e.target.value)}
								label="Employment Type"
							>
								<MenuItem value={EmploymentType.HOURLY}>Hourly</MenuItem>
								<MenuItem value={EmploymentType.SALARY}>Salary</MenuItem>
							</Select>
						</FormControl>
						<FormControl>
							<InputLabel>Role Type</InputLabel>
							<Select
								value={formData.roleType}
								onChange={(e) => handleInputChange('roleType', e.target.value)}
								label="Role Type"
							>
								<MenuItem value={RoleType.PART_TIME}>Part Time</MenuItem>
								<MenuItem value={RoleType.FULL_TIME}>Full Time</MenuItem>
							</Select>
						</FormControl>
					</Box>

					{/* Security & Hierarchy */}
					<Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600 }}>
						Security & Hierarchy
					</Typography>

					{isCurrentUserOrgAdmin &&
						currentUserHierarchyId &&
						permittedHierarchies.length < hierarchyNodes.length && (
						<Box
							sx={{
								mb: 2,
								p: 1.5,
								backgroundColor: '#e3f2fd',
								borderRadius: 1,
								border: '1px solid #90caf9',
							}}
						>
							<Typography variant="body2" sx={{ color: '#1976d2', fontSize: '0.875rem' }}>
								ℹ️ Hierarchy options are filtered based on your permissions. You can only initiate
								onboarding for hierarchy levels at or below your level.
							</Typography>
						</Box>
					)}

					<Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, mb: 3 }}>
						<Autocomplete
							options={[...filteredSecurityRoles].sort((a, b) => a.name.localeCompare(b.name))}
							getOptionLabel={(option) => (typeof option === 'string' ? option : option.name)}
							value={
								filteredSecurityRoles.find((role) => role['@id'] === formData.securityRole) || null
							}
							isOptionEqualToValue={(option, value) => option['@id'] === value?.['@id']}
							onChange={(event, value) => handleInputChange('securityRole', value?.['@id'] || '')}
							renderInput={(params) => (
								<TextField
									{...params}
									label="Mabel Security Role"
									error={!!errors.securityRole}
									helperText={errors.securityRole}
									required
								/>
							)}
						/>
						<Autocomplete
							disabled={!isCurrentUserOrgAdmin}
							options={[...hierarchyOptions].sort((a, b) => {
								// First, sort by type priority: organization > group > workplace
								const getTypePriority = (type: string) => {
									if (type === 'organization') return 0;
									if (type === 'group') return 1;
									if (type === 'workplace') return 2;
									return 3;
								};

								const aPriority = getTypePriority(a.type);
								const bPriority = getTypePriority(b.type);

								if (aPriority !== bPriority) {
									return aPriority - bPriority;
								}

								// If types are the same, sort alphabetically for custom nodes
								if (a.type === 'group' && b.type === 'group') {
									return cleanHierarchyName(a.name).localeCompare(
										cleanHierarchyName(b.name),
										undefined,
										{ sensitivity: 'base' }
									);
								}

								// For workplaces, sort by ID-Name
								if (a.type === 'workplace' && b.type === 'workplace') {
									const aId = a.id || 0;
									const bId = b.id || 0;
									if (aId !== bId) {
										return aId - bId;
									}
									return cleanHierarchyName(a.name).localeCompare(
										cleanHierarchyName(b.name),
										undefined,
										{ sensitivity: 'base' }
									);
								}

								// Default alphabetical sort (cleaned)
								return cleanHierarchyName(a.name).localeCompare(
									cleanHierarchyName(b.name),
									undefined,
									{ sensitivity: 'base' }
								);
							})}
							getOptionLabel={(option) => getHierarchyOptionLabel(option, cleanHierarchyName)}
							value={
								hierarchyOptions?.find((node) => node['@id'] === formData.hierarchyAssignment) ||
								null
							}
							isOptionEqualToValue={(option, value) => option?.['@id'] === value?.['@id']}
							onChange={(event, value) =>
								handleInputChange('hierarchyAssignment', value?.['@id'] || '')
							}
							renderInput={(params) => (
								<TextField
									{...params}
									label="Hierarchy Assignment"
									error={!!errors.hierarchyAssignment}
									helperText={errors.hierarchyAssignment}
									required
								/>
							)}
						/>
					</Box>

					{schedulingLicensed && (
						<Box sx={{ mb: 3 }}>
							<EligibleJobCodesPicker
								jobCodes={jobCodesData}
								selectedIds={selectedJobCodeIds}
								onChange={handleJobCodesChange}
								helperText="Select the Job Code(s) this employee can work. Leave empty if the employee should not appear on the schedule (e.g. above-store roles)."
							/>
						</Box>
					)}

				</form>
			</Paper>
		</div>
	);
}
